import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NetworkDiagram = ({ 
  selectedAddress, 
  filterSettings, 
  onNodeSelect, 
  onPathHighlight,
  exportDiagram 
}) => {
  const svgRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [highlightedPath, setHighlightedPath] = useState([]);
  const [layoutMode, setLayoutMode] = useState('force');

  // Mock network data representing fund flow
  const networkData = {
    nodes: [
      {
        id: 'source',
        address: selectedAddress || '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
        type: 'source',
        amount: 15.75,
        label: 'Source Address',
        risk: 'high',
        x: 400,
        y: 300
      },
      {
        id: 'hop1_1',
        address: '3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy',
        type: 'intermediate',
        amount: 8.25,
        label: 'Intermediate Wallet',
        risk: 'medium',
        x: 600,
        y: 200
      },
      {
        id: 'hop1_2',
        address: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
        type: 'intermediate',
        amount: 7.50,
        label: 'Split Destination',
        risk: 'medium',
        x: 600,
        y: 400
      },
      {
        id: 'mixer1',
        address: 'mixer_service_001',
        type: 'mixer',
        amount: 8.25,
        label: 'Mixing Service',
        risk: 'high',
        x: 800,
        y: 150
      },
      {
        id: 'exchange1',
        address: 'binance_hot_wallet_1',
        type: 'exchange',
        amount: 7.50,
        label: 'Binance Hot Wallet',
        risk: 'low',
        x: 800,
        y: 400
      },
      {
        id: 'final1',
        address: '1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2',
        type: 'destination',
        amount: 5.75,
        label: 'Final Destination',
        risk: 'unknown',
        x: 1000,
        y: 150
      },
      {
        id: 'final2',
        address: '3FupnqMDaQjLcGsrf6kEjSWMrsBqEoJhCx',
        type: 'destination',
        amount: 2.50,
        label: 'Secondary Destination',
        risk: 'medium',
        x: 1000,
        y: 250
      }
    ],
    edges: [
      {
        id: 'e1',
        source: 'source',
        target: 'hop1_1',
        amount: 8.25,
        timestamp: '2024-09-14T10:30:00Z',
        txHash: '0x1234...abcd',
        confidence: 0.95
      },
      {
        id: 'e2',
        source: 'source',
        target: 'hop1_2',
        amount: 7.50,
        timestamp: '2024-09-14T10:32:00Z',
        txHash: '0x5678...efgh',
        confidence: 0.92
      },
      {
        id: 'e3',
        source: 'hop1_1',
        target: 'mixer1',
        amount: 8.25,
        timestamp: '2024-09-14T11:15:00Z',
        txHash: '0x9abc...ijkl',
        confidence: 0.78
      },
      {
        id: 'e4',
        source: 'hop1_2',
        target: 'exchange1',
        amount: 7.50,
        timestamp: '2024-09-14T11:20:00Z',
        txHash: '0xdef0...mnop',
        confidence: 0.88
      },
      {
        id: 'e5',
        source: 'mixer1',
        target: 'final1',
        amount: 5.75,
        timestamp: '2024-09-14T12:45:00Z',
        txHash: '0x1111...qrst',
        confidence: 0.65
      },
      {
        id: 'e6',
        source: 'mixer1',
        target: 'final2',
        amount: 2.50,
        timestamp: '2024-09-14T12:47:00Z',
        txHash: '0x2222...uvwx',
        confidence: 0.68
      }
    ]
  };

  const getNodeColor = (node) => {
    switch (node?.type) {
      case 'source': return '#DC2626'; // red-600
      case 'intermediate': return '#F59E0B'; // amber-500
      case 'mixer': return '#7C3AED'; // violet-600
      case 'exchange': return '#059669'; // emerald-600
      case 'destination': return '#1E3A8A'; // blue-800
      default: return '#64748B'; // slate-500
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return '#DC2626';
      case 'medium': return '#F59E0B';
      case 'low': return '#059669';
      default: return '#64748B';
    }
  };

  const getEdgeWidth = (amount) => {
    const maxAmount = Math.max(...networkData?.edges?.map(e => e?.amount));
    return Math.max(2, (amount / maxAmount) * 8);
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    onNodeSelect?.(node);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.3));
  };

  const handleResetView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleLayoutChange = (mode) => {
    setLayoutMode(mode);
  };

  const handleExport = () => {
    exportDiagram?.(svgRef?.current);
  };

  return (
    <div className="bg-surface rounded-lg border border-border shadow-forensic-card">
      {/* Control Panel */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-text-primary">Fund Flow Network</h3>
          <div className="flex items-center space-x-1 text-sm text-text-secondary">
            <Icon name="GitBranch" size={16} />
            <span>{networkData?.nodes?.length} addresses</span>
            <span>•</span>
            <span>{networkData?.edges?.length} transactions</span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Layout Controls */}
          <div className="flex items-center space-x-1 mr-4">
            <Button
              variant={layoutMode === 'force' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleLayoutChange('force')}
            >
              Force
            </Button>
            <Button
              variant={layoutMode === 'hierarchical' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleLayoutChange('hierarchical')}
            >
              Tree
            </Button>
            <Button
              variant={layoutMode === 'circular' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => handleLayoutChange('circular')}
            >
              Circular
            </Button>
          </div>

          {/* Zoom Controls */}
          <Button variant="ghost" size="sm" onClick={handleZoomOut}>
            <Icon name="ZoomOut" size={16} />
          </Button>
          <span className="text-sm text-text-secondary min-w-12 text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          <Button variant="ghost" size="sm" onClick={handleZoomIn}>
            <Icon name="ZoomIn" size={16} />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleResetView}>
            <Icon name="RotateCcw" size={16} />
          </Button>

          <div className="w-px h-6 bg-border mx-2" />

          <Button variant="outline" size="sm" onClick={handleExport}>
            <Icon name="Download" size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>
      {/* Network Visualization */}
      <div className="relative h-96 lg:h-[600px] overflow-hidden">
        <svg
          ref={svgRef}
          className="w-full h-full"
          viewBox={`${-panOffset?.x} ${-panOffset?.y} ${1200 / zoomLevel} ${600 / zoomLevel}`}
        >
          {/* Grid Background */}
          <defs>
            <pattern
              id="grid"
              width="50"
              height="50"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 50 0 L 0 0 0 50"
                fill="none"
                stroke="#E2E8F0"
                strokeWidth="1"
                opacity="0.3"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Edges */}
          {networkData?.edges?.map((edge) => {
            const sourceNode = networkData?.nodes?.find(n => n?.id === edge?.source);
            const targetNode = networkData?.nodes?.find(n => n?.id === edge?.target);
            
            if (!sourceNode || !targetNode) return null;

            const isHighlighted = highlightedPath?.includes(edge?.id);
            
            return (
              <g key={edge?.id}>
                <line
                  x1={sourceNode?.x}
                  y1={sourceNode?.y}
                  x2={targetNode?.x}
                  y2={targetNode?.y}
                  stroke={isHighlighted ? '#1E3A8A' : '#94A3B8'}
                  strokeWidth={getEdgeWidth(edge?.amount)}
                  opacity={isHighlighted ? 1 : 0.6}
                  markerEnd="url(#arrowhead)"
                />
                {/* Edge Label */}
                <text
                  x={(sourceNode?.x + targetNode?.x) / 2}
                  y={(sourceNode?.y + targetNode?.y) / 2 - 10}
                  textAnchor="middle"
                  className="text-xs fill-text-secondary"
                  fontSize="10"
                >
                  {edge?.amount} BTC
                </text>
              </g>
            );
          })}

          {/* Arrow Marker */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill="#94A3B8"
              />
            </marker>
          </defs>

          {/* Nodes */}
          {networkData?.nodes?.map((node) => {
            const isSelected = selectedNode?.id === node?.id;
            
            return (
              <g key={node?.id}>
                {/* Node Circle */}
                <circle
                  cx={node?.x}
                  cy={node?.y}
                  r={isSelected ? 25 : 20}
                  fill={getNodeColor(node)}
                  stroke={isSelected ? '#1E3A8A' : 'white'}
                  strokeWidth={isSelected ? 3 : 2}
                  className="cursor-pointer transition-all duration-200"
                  onClick={() => handleNodeClick(node)}
                />
                {/* Node Icon */}
                <text
                  x={node?.x}
                  y={node?.y + 4}
                  textAnchor="middle"
                  className="fill-white text-sm font-semibold pointer-events-none"
                  fontSize="12"
                >
                  {node?.type === 'source' ? 'S' :
                   node?.type === 'mixer' ? 'M' :
                   node?.type === 'exchange' ? 'E' :
                   node?.type === 'destination' ? 'D' : 'I'}
                </text>
                {/* Node Label */}
                <text
                  x={node?.x}
                  y={node?.y + 35}
                  textAnchor="middle"
                  className="text-xs fill-text-primary font-medium"
                  fontSize="11"
                >
                  {node?.label}
                </text>
                {/* Amount Label */}
                <text
                  x={node?.x}
                  y={node?.y + 48}
                  textAnchor="middle"
                  className="text-xs fill-text-secondary"
                  fontSize="10"
                >
                  {node?.amount} BTC
                </text>
                {/* Risk Indicator */}
                <circle
                  cx={node?.x + 15}
                  cy={node?.y - 15}
                  r="6"
                  fill={getRiskColor(node?.risk)}
                  stroke="white"
                  strokeWidth="2"
                />
              </g>
            );
          })}
        </svg>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-surface border border-border rounded-lg p-3 shadow-forensic-card">
          <h4 className="text-sm font-semibold text-text-primary mb-2">Legend</h4>
          <div className="space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-600"></div>
              <span className="text-text-secondary">Source Address</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-text-secondary">Intermediate</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-violet-600"></div>
              <span className="text-text-secondary">Mixing Service</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-emerald-600"></div>
              <span className="text-text-secondary">Exchange</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-800"></div>
              <span className="text-text-secondary">Destination</span>
            </div>
          </div>
        </div>
      </div>
      {/* Selected Node Details */}
      {selectedNode && (
        <div className="p-4 border-t border-border bg-muted">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-text-primary mb-2">
                {selectedNode?.label}
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-text-secondary">Address:</span>
                  <p className="font-mono text-text-primary break-all">
                    {selectedNode?.address}
                  </p>
                </div>
                <div>
                  <span className="text-text-secondary">Amount:</span>
                  <p className="text-text-primary font-semibold">
                    {selectedNode?.amount} BTC
                  </p>
                </div>
                <div>
                  <span className="text-text-secondary">Type:</span>
                  <p className="text-text-primary capitalize">
                    {selectedNode?.type}
                  </p>
                </div>
                <div>
                  <span className="text-text-secondary">Risk Level:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    selectedNode?.risk === 'high' ? 'bg-red-100 text-red-800' :
                    selectedNode?.risk === 'medium' ? 'bg-amber-100 text-amber-800' :
                    selectedNode?.risk === 'low'? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'
                  }`}>
                    {selectedNode?.risk}
                  </span>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedNode(null)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetworkDiagram;